/**
 * Map a stage's lifecycle role to a colour bucket.
 *
 * Two ways to resolve a colour:
 *
 * 1. **Explicit `kind`** (preferred). Adapters / engine label each stage with a
 *    `StageKind` describing where in the deal lifecycle it sits. This is
 *    robust to pipelines of any length and shape, and is the only way to
 *    colour terminal lost states correctly.
 *
 * 2. **Position-based fallback**. When no kind is given, the stage's normalised
 *    position in the pipeline picks a bucket from the same palette. The fallback
 *    will *never* return `lost` — terminal lost states require explicit metadata.
 *
 * Each bucket exposes a `(base, active)` pair: `base` is the muted tone for
 * unselected stages, `active` is the saturated tone the selected stage gets.
 * Tokens live in `packages/ui/src/themes/dark/index.css`.
 */

export type StageKind = 'intake' | 'cool' | 'mid' | 'warm' | 'won' | 'lost'

export interface StageColor {
  base: string
  active: string
}

const BUCKET_COLORS: Record<StageKind, StageColor> = {
  intake: {
    base: 'var(--color-stage-intake-base)',
    active: 'var(--color-stage-intake-active)',
  },
  cool: {
    base: 'var(--color-stage-cool-base)',
    active: 'var(--color-stage-cool-active)',
  },
  mid: {
    base: 'var(--color-stage-mid-base)',
    active: 'var(--color-stage-mid-active)',
  },
  warm: {
    base: 'var(--color-stage-warm-base)',
    active: 'var(--color-stage-warm-active)',
  },
  won: {
    base: 'var(--color-stage-won-base)',
    active: 'var(--color-stage-won-active)',
  },
  lost: {
    base: 'var(--color-stage-lost-base)',
    active: 'var(--color-stage-lost-active)',
  },
}

/**
 * Position-based fallback. Returns a bucket from the lifecycle palette based on
 * the stage's normalised position. Never resolves to `lost`.
 */
export function getStageBucketByPosition(index: number, total: number): Exclude<StageKind, 'lost'> {
  if (total <= 1) return 'mid'
  const p = index / (total - 1)
  if (p < 0.15) return 'intake'
  if (p < 0.42) return 'cool'
  if (p < 0.72) return 'mid'
  if (p < 0.88) return 'warm'
  return 'won'
}

export function getStageColor(index: number, total: number, kind?: StageKind): StageColor {
  return BUCKET_COLORS[kind ?? getStageBucketByPosition(index, total)]
}
